/**
 * Cash Tracker — linear dense tables, not stacked forms.
 * View & export prefs (column widths + sections) live in localStorage only.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { CopySimple, MagnifyingGlass, Plus, Printer, Trash, Warning, X } from '@phosphor-icons/react';
import { formatMoney, formatPercent } from '../utils/formatters';
import {
  EMPTY_COLUMN_FILTERS,
  filterCashItems,
  hasActiveColumnFilters,
  sumCashItemsNet,
} from '../utils/cashItemFilters';
import {
  CASH_TX_COLUMN_KEYS,
  CASH_VIEW_SECTION_DEFS,
  clampCashTxColumnWidth,
  getCashPrintSelection,
  loadCashTxColumnWidths,
  loadCashViewSections,
  resetCashTxColumnWidths,
  resolveCashExportSections,
  saveCashTxColumnWidths,
  saveCashViewSections,
  totalCashTxTableWidth,
} from '../utils/cashTrackerViewPrefs';
import { fetchDeals } from './SnapshotManager';
import { CashAmountInput, CashSelect, CashTextInput } from './cashFields';
import {
  BASELINE_CATEGORY_DEFS,
  CASH_DIRECTIONS,
  CASH_STATUSES,
  DIRECTION_LABEL,
  ENTITY_ROLES,
  ENTITY_ROLE_LABEL,
  STATUS_LABEL,
  addEntity,
  categoryDef,
  compactCashSummary,
  duplicateCashItem,
  emptyCashItem,
  evaluationReconcile,
  formatCashDisplayDate,
  hydrateCashTracker,
  isCashDisplayDateInput,
  listEntities,
  materialExceptions,
  parseCashIsoDate,
  portfolioCash,
  removeCashItem,
  removeEntity,
  renameEntity,
  setDesignatedReserves,
  setEntityOpening,
  setEntityRole,
  summarizeByEntity,
  summarizeCash,
  upsertCashItem,
} from '../utils/cashTracker';

/** Stable accessible names for cash row editor fields (agent + a11y contract). */
export const CASH_ITEM_FIELD_LABELS = {
  date: 'Date (DD.MM.YYYY)',
  direction: 'Direction',
  entity: 'Entity',
  counterparty: 'Counterparty',
  category: 'Category',
  amount: 'Amount',
  status: 'Status',
  note: 'Note',
  duplicate: 'Duplicate cash row',
  delete: 'Delete cash row',
  cancel: 'Cancel cash edit',
};

const STATUS_TONE = {
  expected: 'text-slate-600',
  due: 'text-amber-700 font-semibold',
  paid: 'text-emerald-700',
  cancelled: 'text-gray-400 line-through',
};

const COLUMN_SEARCH_CLASS =
  'box-border block w-full min-w-0 h-6 rounded border border-gray-200 bg-white px-1.5 text-[11px] font-normal normal-case tracking-normal text-gray-800 placeholder:text-gray-300';

function ColumnSearch({ value, onChange, placeholder, testId, list, align }) {
  return (
    <input
      type="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      list={list}
      autoComplete="off"
      spellCheck={false}
      data-testid={testId}
      aria-label={placeholder}
      className={`${COLUMN_SEARCH_CLASS} ${align === 'right' ? 'text-right' : ''}`}
    />
  );
}

function MoneyCell({ children, className = '', testId }) {
  return (
    <td className={`overflow-hidden px-3 py-1 text-right tabular-nums ${className}`} data-testid={testId}>
      <span className="block truncate" title={typeof children === 'string' ? children : undefined}>{children}</span>
    </td>
  );
}

export { getCashPrintSelection };

const TX_COLUMN_LABELS = {
  date: 'Date',
  direction: 'Dir',
  entity: 'Entity',
  counterparty: 'Counterparty',
  category: 'Category',
  amount: 'Amount',
  status: 'Status',
  note: 'Note',
  actions: 'Actions',
};

function ResizableColumnHead({ colKey, label, align, onResizeStart, hideLabel }) {
  return (
    <th
      className={`relative px-1.5 py-1 font-semibold ${align === 'right' ? 'text-right' : ''}`}
      data-testid={`cash-col-head-${colKey}`}
    >
      <span className={`pr-2 ${hideLabel ? 'sr-only' : ''}`}>{label}</span>
      <span
        role="separator"
        aria-orientation="vertical"
        aria-label={`Resize ${label || colKey} column`}
        data-testid={`cash-col-resize-${colKey}`}
        className="cash-no-print absolute right-0 top-0 z-10 h-full w-1.5 cursor-col-resize touch-none hover:bg-indigo-300/70 active:bg-indigo-400/80"
        onMouseDown={(e) => onResizeStart(colKey, e)}
      />
    </th>
  );
}

function formatPrintGeneratedAt(date = new Date()) {
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  } catch {
    return date.toISOString();
  }
}

export function CashTrackerView({
  cashTracker,
  dealId,
  dealName,
  onChange,
}) {
  const tracker = useMemo(() => hydrateCashTracker(cashTracker), [cashTracker]);
  const summary = useMemo(() => summarizeCash(tracker), [tracker]);
  const evaluation = useMemo(() => evaluationReconcile(tracker), [tracker]);
  const exceptions = useMemo(() => materialExceptions(tracker), [tracker]);
  const entities = useMemo(() => listEntities(tracker, dealName || 'Development'), [tracker, dealName]);
  const entityRows = useMemo(() => summarizeByEntity(tracker), [tracker]);
  const currency = summary.currency || 'CHF';
  const money = (n) => formatMoney(n, currency);

  const [filter, setFilter] = useState('all');
  const [entityFilter, setEntityFilter] = useState('all');
  const [columnFilters, setColumnFilters] = useState(EMPTY_COLUMN_FILTERS);
  const [editing, setEditing] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [showOpening, setShowOpening] = useState(false);
  const [columnWidths, setColumnWidths] = useState(() => loadCashTxColumnWidths());
  const [viewSections, setViewSections] = useState(() => loadCashViewSections());
  const [viewMenuOpen, setViewMenuOpen] = useState(false);
  const viewMenuRef = useRef(null);
  const resizeDragRef = useRef(null);

  const visibleSections = useMemo(
    () => resolveCashExportSections(viewSections),
    [viewSections],
  );
  const printSelection = useMemo(
    () => getCashPrintSelection(viewSections, columnWidths),
    [viewSections, columnWidths],
  );
  const tableWidthPx = totalCashTxTableWidth(columnWidths);

  useEffect(() => {
    let cancelled = false;
    fetchDeals().then((deals) => {
      if (cancelled) return;
      const launched = (deals || []).filter((d) => d.launched || d.cashSummary?.launched);
      const current = compactCashSummary(tracker);
      const merged = launched.map((d) => (
        d.id === dealId && current ? { ...d, cashSummary: current } : d
      ));
      if (dealId && current && !merged.some((d) => d.id === dealId)) {
        merged.unshift({ id: dealId, name: dealName, cashSummary: current });
      }
      setPortfolio(portfolioCash(merged));
    });
    return () => { cancelled = true; };
  }, [dealId, dealName, tracker]);

  useEffect(() => {
    if (!viewMenuOpen) return undefined;
    const onPointerDown = (event) => {
      if (viewMenuRef.current && !viewMenuRef.current.contains(event.target)) {
        setViewMenuOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setViewMenuOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [viewMenuOpen]);

  useEffect(() => () => {
    const drag = resizeDragRef.current;
    if (!drag) return;
    document.removeEventListener('mousemove', drag.onMove);
    document.removeEventListener('mouseup', drag.onUp);
  }, []);

  if (!tracker) return null;

  const items = [...tracker.items].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  const chipFiltered = items.filter((item) => {
    if (entityFilter !== 'all' && item.entity !== entityFilter) return false;
    if (filter === 'all') return item.status !== 'cancelled';
    if (filter === 'unpaid') return item.status === 'expected' || item.status === 'due';
    return item.status === filter;
  });
  const columnsActive = hasActiveColumnFilters(columnFilters);
  const visible = filterCashItems(chipFiltered, columnFilters, {
    categoryLabel: (item) => categoryDef(item.categoryKey).label,
    amountText: (item) => money(item.amount),
    dateText: (item) => formatCashDisplayDate(item.date),
  });
  // Status/entity chips or column searches count as an active filter for the Total row.
  const listFiltersActive = columnsActive || filter !== 'all' || entityFilter !== 'all';
  const visibleNetTotal = sumCashItemsNet(visible);
  const setColumnFilter = (key, value) => setColumnFilters((current) => ({ ...current, [key]: value }));
  const clearColumnFilters = () => setColumnFilters(EMPTY_COLUMN_FILTERS);
  const commit = (next) => onChange(next);
  const startEdit = (item) => setEditing({
    ...item,
    date: formatCashDisplayDate(item.date) || item.date || '',
  });
  const startNew = () => startEdit(emptyCashItem({ entity: entities[0] }));
  const saveRow = (draft) => {
    if (!(Number(draft.amount) > 0)) return;
    // Editor accepts the documented DD.MM.YYYY workflow only (not ISO paste).
    if (!isCashDisplayDateInput(draft.date)) return;
    const date = parseCashIsoDate(draft.date);
    if (!date) return;
    commit(upsertCashItem(tracker, { ...draft, date }));
    setEditing(null);
  };

  const persistColumnWidths = (next) => {
    const saved = saveCashTxColumnWidths(next);
    setColumnWidths(saved);
    return saved;
  };

  const onColumnResizeStart = (key, event) => {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startWidth = columnWidths[key];
    const previous = resizeDragRef.current;
    if (previous) {
      document.removeEventListener('mousemove', previous.onMove);
      document.removeEventListener('mouseup', previous.onUp);
    }
    const onMove = (moveEvent) => {
      const nextWidth = clampCashTxColumnWidth(key, startWidth + (moveEvent.clientX - startX));
      setColumnWidths((current) => (
        current[key] === nextWidth ? current : { ...current, [key]: nextWidth }
      ));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      resizeDragRef.current = null;
      setColumnWidths((current) => saveCashTxColumnWidths(current));
    };
    resizeDragRef.current = { onMove, onUp };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const resetColumns = () => {
    setColumnWidths(resetCashTxColumnWidths());
  };

  const setSectionVisible = (key, checked) => {
    setViewSections((current) => saveCashViewSections({ ...current, [key]: checked }));
  };

  const printCashExtract = () => {
    setViewMenuOpen(false);
    window.setTimeout(() => {
      window.print();
    }, 0);
  };

  return (
    <div
      className="w-full max-w-[1400px] mx-auto px-2 sm:px-3 pb-16"
      data-testid="cash-tracker-view"
      data-cash-print-sections={JSON.stringify(printSelection.sections)}
      data-cash-table-width={String(tableWidthPx)}
    >
      <header className="hidden cash-print-only" data-testid="cash-print-header">
        <h1>{dealName || 'Cash Tracker'}</h1>
        <p>
          Cash Tracker extract · Generated {formatPrintGeneratedAt()}
          {tracker.approvedBaseline?.scenarioName
            ? ` · ${tracker.approvedBaseline.scenarioOrdinal}. ${tracker.approvedBaseline.scenarioName}`
            : ''}
        </p>
      </header>

      <div className="flex items-center gap-2 py-1.5 min-w-0 cash-no-print">
        <h1 className="text-[13px] font-semibold text-gray-900 shrink-0">Cash Tracker</h1>
        <p className="text-[11px] text-gray-500 truncate min-w-0">
          {tracker.approvedBaseline.scenarioOrdinal}. {tracker.approvedBaseline.scenarioName}
          <span className="text-gray-400"> · frozen</span>
        </p>
        <div className="ml-auto flex items-center gap-1.5 shrink-0">
          <div className="relative" ref={viewMenuRef}>
            <button
              type="button"
              onClick={() => setViewMenuOpen((open) => !open)}
              className="inline-flex items-center gap-1 h-8 px-2.5 rounded border border-gray-300 bg-white text-gray-800 text-[12px] font-semibold hover:bg-gray-50"
              data-testid="cash-view-export"
              aria-expanded={viewMenuOpen}
              aria-haspopup="dialog"
            >
              View &amp; export
            </button>
            {viewMenuOpen ? (
              <div
                className="absolute right-0 z-30 mt-1 w-[17.5rem] rounded border border-gray-200 bg-white shadow-lg p-2"
                role="dialog"
                aria-label="View and export"
                data-testid="cash-view-export-panel"
              >
                <p className="px-1 pb-1 text-[10px] uppercase tracking-wide text-gray-400 font-semibold">
                  Tracker &amp; print sections
                </p>
                <ul className="space-y-0.5">
                  {CASH_VIEW_SECTION_DEFS.map((def) => {
                    const onlyTransactionsOn = viewSections.transactions
                      && !Object.entries(viewSections).some(([k, v]) => k !== 'transactions' && v);
                    return (
                      <li key={def.key}>
                        <label
                          className="flex items-center gap-2 px-1 py-1 text-[12px] text-gray-800 cursor-pointer hover:bg-slate-50"
                          data-testid={`cash-section-toggle-${def.key}`}
                        >
                          <input
                            type="checkbox"
                            checked={!!viewSections[def.key]}
                            disabled={def.primary && onlyTransactionsOn}
                            onChange={(e) => setSectionVisible(def.key, e.target.checked)}
                            data-testid={`cash-section-${def.key}`}
                          />
                          <span>
                            {def.label}
                            {def.primary ? <span className="text-gray-400"> · primary</span> : null}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
                <div className="mt-2 pt-2 border-t border-gray-100 space-y-1">
                  <button
                    type="button"
                    className="w-full inline-flex items-center justify-center gap-1.5 h-8 rounded bg-gray-900 text-white text-[12px] font-semibold"
                    data-testid="cash-print-pdf"
                    onClick={printCashExtract}
                  >
                    <Printer weight="bold" className="w-3.5 h-3.5" />
                    Print / Save PDF
                  </button>
                  <button
                    type="button"
                    className="w-full h-8 rounded border border-gray-200 text-[12px] font-medium text-gray-700 hover:bg-gray-50"
                    data-testid="cash-reset-columns"
                    onClick={resetColumns}
                  >
                    Reset columns
                  </button>
                </div>
              </div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={startNew}
            className="shrink-0 inline-flex items-center gap-1 h-8 px-2.5 rounded bg-gray-900 text-white text-[12px] font-semibold"
            data-testid="cash-add-item"
            aria-label="Add cash row"
          >
            <Plus weight="bold" className="w-3.5 h-3.5" />
            Row
          </button>
        </div>
      </div>

      {exceptions.length > 0 && (
        <div className="mb-1.5 border border-amber-200 bg-amber-50 px-2 py-1" data-testid="cash-exceptions">
          {exceptions.map((ex) => (
            <div key={`${ex.type}-${ex.key || ''}`} className="flex items-center gap-1.5 text-[12px] text-amber-900">
              <Warning weight="fill" className="w-3 h-3 shrink-0" />
              <span className="truncate">{ex.label}</span>
              <span className="ml-auto tabular-nums font-semibold">{money(ex.amount)}</span>
            </div>
          ))}
        </div>
      )}

      {visibleSections.summary ? (
<section className="border border-gray-200 bg-white overflow-x-auto" data-testid="cash-summary">
        <table className="w-full min-w-[800px] table-fixed text-[12px] tabular-nums">
          <thead>
            <tr className="text-[10px] uppercase tracking-wide text-gray-400 text-left">
              <th className="truncate px-2 py-1 font-semibold" title="Opening cash balances">Opening</th>
              <th className="truncate px-2 py-1 font-semibold" title="Paid inflows only">Cash in (paid)</th>
              <th className="truncate px-2 py-1 font-semibold" title="Paid outflows only">Cash out (paid)</th>
              <th className="truncate px-2 py-1 font-semibold" title="Opening + paid in − paid out">Cash today</th>
              <th
                className="truncate px-2 py-1 font-semibold"
                title="Cash today plus every expected/due ledger item — forecast, not available today"
              >
                Future cash
              </th>
              <th
                className="truncate px-2 py-1 font-semibold"
                title="Cash today after due obligations and designated reserves — never counts expected cash"
              >
                Deployable cash
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-gray-100">
              <MoneyCell className="font-semibold" testId="cash-opening">{money(summary.opening)}</MoneyCell>
              <MoneyCell className="text-emerald-700" testId="cash-in-paid">{money(summary.cashIn)}</MoneyCell>
              <MoneyCell testId="cash-out-paid">{money(summary.cashOut)}</MoneyCell>
              <MoneyCell className={`font-semibold ${summary.cashTodayNegative ? 'text-red-700' : ''}`} testId="cash-today">
                {money(summary.cashToday)}
              </MoneyCell>
              <MoneyCell testId="cash-future">{money(summary.futureCash)}</MoneyCell>
              <MoneyCell testId="cash-deployable">{money(summary.deployableCash)}</MoneyCell>
            </tr>
          </tbody>
        </table>
      </section>
      ) : null}

      {(visibleSections.balances || visibleSections.entities) ? (
        <EntitiesTable
          rows={entityRows}
          money={money}
          canDelete={entityRows.length > 1}
          showBalances={visibleSections.balances}
          showEntities={visibleSections.entities}
          onAdd={() => commit(addEntity(tracker, { name: 'Entity', role: 'other' }))}
          onRename={(id, name) => commit(renameEntity(tracker, id, name))}
          onRole={(id, role) => commit(setEntityRole(tracker, id, role))}
          onOpening={(id, amount) => commit(setEntityOpening(tracker, id, amount))}
          onRemove={(id) => {
            if (entityFilter !== 'all' && entityRows.find((r) => r.id === id)?.name === entityFilter) {
              setEntityFilter('all');
            }
            commit(removeEntity(tracker, id));
          }}
          reserves={tracker.designatedReserves}
          onReserves={(amount) => commit(setDesignatedReserves(tracker, amount))}
          showReserves={showOpening}
          onToggleReserves={() => setShowOpening((v) => !v)}
        />
      ) : null}

      {visibleSections.transactions ? (
<section className="mt-2 border border-gray-200 bg-white" data-testid="cash-item-list">
        <div className="flex flex-wrap items-center gap-1 px-2 py-1 border-b border-gray-100 cash-no-print" data-testid="cash-filters">
          <span className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold mr-1">Items</span>
          {['all', 'unpaid', 'due', 'paid'].map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => setFilter(id)}
              className={`h-6 px-2 text-[11px] font-medium ${
                filter === id ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-800'
              }`}
            >
              {id === 'all' ? 'All' : id === 'unpaid' ? 'Unpaid' : STATUS_LABEL[id]}
            </button>
          ))}
          <span className="w-px h-4 bg-gray-200 mx-1" />
          <button
            type="button"
            onClick={() => setEntityFilter('all')}
            className={`h-6 px-2 text-[11px] font-medium ${
              entityFilter === 'all' ? 'bg-indigo-900 text-white' : 'text-gray-500 hover:text-gray-800'
            }`}
            data-testid="cash-entity-filter-all"
          >
            All entities
          </button>
          {entities.map((name) => (
            <button
              key={name}
              type="button"
              onClick={() => setEntityFilter(name)}
              className={`h-6 px-2 text-[11px] font-medium max-w-[8rem] truncate ${
                entityFilter === name ? 'bg-indigo-900 text-white' : 'text-gray-500 hover:text-gray-800'
              }`}
              data-testid={`cash-entity-filter-${name}`}
            >
              {name}
            </button>
          ))}
          <span className="ml-auto flex items-center gap-1.5 text-[11px] text-gray-400">
            <MagnifyingGlass className="w-3 h-3" />
            {columnsActive ? `${visible.length} of ${chipFiltered.length}` : `${visible.length} rows`}
            {columnsActive ? (
              <button
                type="button"
                className="text-indigo-700 hover:underline"
                data-testid="cash-clear-column-filters"
                onClick={clearColumnFilters}
              >
                Clear
              </button>
            ) : null}
            <button
              type="button"
              className="text-indigo-700 hover:underline"
              data-testid="cash-reset-columns-inline"
              onClick={resetColumns}
            >
              Reset columns
            </button>
          </span>
        </div>
        <div className="overflow-x-auto">
          <datalist id="cash-filter-dates">{[...new Set(chipFiltered.map((i) => formatCashDisplayDate(i.date)).filter(Boolean))].map((v) => <option key={v} value={v} />)}</datalist>
          <datalist id="cash-filter-dirs">
            <option value="In" />
            <option value="Out" />
          </datalist>
          <datalist id="cash-filter-entities">{entities.map((v) => <option key={v} value={v} />)}</datalist>
          <datalist id="cash-filter-parties">{[...new Set(chipFiltered.map((i) => i.counterparty).filter(Boolean))].map((v) => <option key={v} value={v} />)}</datalist>
          <datalist id="cash-filter-categories">{[...new Set(chipFiltered.map((i) => categoryDef(i.categoryKey).label))].map((v) => <option key={v} value={v} />)}</datalist>
          <datalist id="cash-filter-statuses">
            {['Expected', 'Due', 'Paid'].map((v) => <option key={v} value={v} />)}
          </datalist>
          <table
            className="table-fixed border-collapse text-[12px]"
            style={{ width: tableWidthPx, minWidth: tableWidthPx }}
            data-testid="cash-tx-table"
          >
            <colgroup>
              {CASH_TX_COLUMN_KEYS.map((key) => (
                <col
                  key={key}
                  style={{ width: columnWidths[key] }}
                  data-testid={`cash-col-${key}`}
                  data-cash-col-width={String(columnWidths[key])}
                />
              ))}
            </colgroup>
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-gray-400 text-left border-b border-gray-100">
                <ResizableColumnHead colKey="date" label={TX_COLUMN_LABELS.date} onResizeStart={onColumnResizeStart} />
                <ResizableColumnHead colKey="direction" label={TX_COLUMN_LABELS.direction} onResizeStart={onColumnResizeStart} />
                <ResizableColumnHead colKey="entity" label={TX_COLUMN_LABELS.entity} onResizeStart={onColumnResizeStart} />
                <ResizableColumnHead colKey="counterparty" label={TX_COLUMN_LABELS.counterparty} onResizeStart={onColumnResizeStart} />
                <ResizableColumnHead colKey="category" label={TX_COLUMN_LABELS.category} onResizeStart={onColumnResizeStart} />
                <ResizableColumnHead colKey="amount" label={TX_COLUMN_LABELS.amount} align="right" onResizeStart={onColumnResizeStart} />
                <ResizableColumnHead colKey="status" label={TX_COLUMN_LABELS.status} onResizeStart={onColumnResizeStart} />
                <ResizableColumnHead colKey="note" label={TX_COLUMN_LABELS.note} onResizeStart={onColumnResizeStart} />
                <ResizableColumnHead colKey="actions" label={TX_COLUMN_LABELS.actions} hideLabel onResizeStart={onColumnResizeStart} />
              </tr>
              <tr className="border-b border-gray-100 bg-slate-50/80 cash-no-print" data-testid="cash-column-filters">
                <th className="px-1 py-1 font-normal">
                  <ColumnSearch value={columnFilters.date} onChange={(v) => setColumnFilter('date', v)} list="cash-filter-dates" testId="cash-filter-date" placeholder="Search date" />
                </th>
                <th className="px-1 py-1 font-normal">
                  <ColumnSearch value={columnFilters.direction} onChange={(v) => setColumnFilter('direction', v)} list="cash-filter-dirs" testId="cash-filter-direction" placeholder="In / Out" />
                </th>
                <th className="px-1 py-1 font-normal">
                  <ColumnSearch value={columnFilters.entity} onChange={(v) => setColumnFilter('entity', v)} list="cash-filter-entities" testId="cash-filter-entity" placeholder="Search" />
                </th>
                <th className="px-1 py-1 font-normal">
                  <ColumnSearch value={columnFilters.counterparty} onChange={(v) => setColumnFilter('counterparty', v)} list="cash-filter-parties" testId="cash-filter-counterparty" placeholder="Search" />
                </th>
                <th className="px-1 py-1 font-normal">
                  <ColumnSearch value={columnFilters.category} onChange={(v) => setColumnFilter('category', v)} list="cash-filter-categories" testId="cash-filter-category" placeholder="Search" />
                </th>
                <th className="px-1 py-1 font-normal">
                  <ColumnSearch value={columnFilters.amount} onChange={(v) => setColumnFilter('amount', v)} testId="cash-filter-amount" placeholder="Search" align="right" />
                </th>
                <th className="px-1 py-1 font-normal">
                  <ColumnSearch value={columnFilters.status} onChange={(v) => setColumnFilter('status', v)} list="cash-filter-statuses" testId="cash-filter-status" placeholder="Search" />
                </th>
                <th className="px-1 py-1 font-normal">
                  <ColumnSearch value={columnFilters.note} onChange={(v) => setColumnFilter('note', v)} testId="cash-filter-note" placeholder="Search" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {editing && !tracker.items.some((i) => i.id === editing.id) ? (
                <EditItemRow
                  draft={editing}
                  entities={entities}
                  isNew
                  onChange={setEditing}
                  onSave={(draft) => saveRow(draft)}
                  onCancel={() => setEditing(null)}
                />
              ) : null}
              {visible.map((item) => (
                editing?.id === item.id ? (
                  <EditItemRow
                    key={item.id}
                    draft={editing}
                    entities={entities}
                    onChange={setEditing}
                    onSave={(draft) => saveRow(draft)}
                    onCancel={() => setEditing(null)}
                    onDuplicate={() => {
                      const next = duplicateCashItem(tracker, item);
                      const known = new Set(tracker.items.map((row) => row.id));
                      const copy = next.items.find((row) => !known.has(row.id));
                      commit(next);
                      if (copy) startEdit(copy);
                    }}
                    onDelete={() => {
                      commit(removeCashItem(tracker, item.id));
                      setEditing(null);
                    }}
                  />
                ) : (
                  <tr
                    key={item.id}
                    className="border-t border-gray-50 hover:bg-slate-50 cursor-pointer"
                    data-testid={`cash-item-${item.id}`}
                    data-cash-row-id={item.id}
                    data-cash-state="saved"
                    data-cash-date={formatCashDisplayDate(item.date) || ''}
                    tabIndex={0}
                    aria-label={`Edit cash row ${formatCashDisplayDate(item.date) || item.date}`}
                    onClick={() => startEdit(item)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        startEdit(item);
                      }
                    }}
                  >
                    <td
                      className="overflow-hidden px-1.5 py-1 whitespace-nowrap tabular-nums text-gray-700"
                      data-testid={`cash-item-${item.id}-date`}
                    >
                      {formatCashDisplayDate(item.date) || item.date}
                    </td>
                    <td className={`overflow-hidden truncate px-1.5 py-1 ${item.direction === 'in' ? 'text-emerald-700' : 'text-gray-700'}`}>
                      {DIRECTION_LABEL[item.direction]}
                    </td>
                    <td className="overflow-hidden truncate px-1.5 py-1">{item.entity}</td>
                    <td className="overflow-hidden truncate px-1.5 py-1">{item.counterparty || '—'}</td>
                    <td className="overflow-hidden truncate px-1.5 py-1">{categoryDef(item.categoryKey).label}</td>
                    <td className={`overflow-hidden px-1.5 py-1 text-right tabular-nums font-medium ${
                      item.direction === 'in' ? 'text-emerald-700' : 'text-gray-900'
                    }`}>
                      <span className="block truncate">{item.direction === 'in' ? '+' : '−'}{money(item.amount)}</span>
                    </td>
                    <td className={`overflow-hidden truncate px-1.5 py-1 ${STATUS_TONE[item.status]}`}>{STATUS_LABEL[item.status]}</td>
                    <td className="overflow-hidden truncate px-1.5 py-1 text-gray-500">{item.note || item.documentName || ''}</td>
                    <td className="px-1 py-0.5" />
                  </tr>
                )
              ))}
              {visible.length === 0 && !(editing && !tracker.items.some((i) => i.id === editing.id)) ? (
                <tr>
                  <td colSpan={9} className="px-2 py-4 text-center text-[12px] text-gray-400">
                    {chipFiltered.length === 0
                      ? 'No rows yet. Add the first cash movement.'
                      : 'No rows match these filters.'}
                  </td>
                </tr>
              ) : null}
              {listFiltersActive && visible.length > 0 ? (
                <tr
                  className="border-t border-gray-200 bg-slate-50 font-semibold"
                  data-testid="cash-filter-total"
                >
                  <td className="px-1.5 py-1.5 text-gray-700" colSpan={5}>
                    Total
                    <span className="ml-1.5 font-normal text-gray-400">
                      {visible.length} {visible.length === 1 ? 'row' : 'rows'}
                    </span>
                  </td>
                  <td
                    className={`overflow-hidden px-1.5 py-1.5 text-right tabular-nums ${
                      visibleNetTotal > 0 ? 'text-emerald-700' : 'text-gray-900'
                    }`}
                    data-testid="cash-filter-total-amount"
                  >
                    <span className="block truncate">
                      {visibleNetTotal > 0 ? '+' : visibleNetTotal < 0 ? '−' : ''}
                      {money(Math.abs(visibleNetTotal))}
                    </span>
                  </td>
                  <td colSpan={3} />
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>
      ) : null}

      {visibleSections.reconciliation ? (
        <EvaluationCashTable evaluation={evaluation} money={money} />
      ) : null}

      {portfolio && portfolio.developments.length > 0 && (
        <section className="mt-2 border border-gray-200 bg-white overflow-x-auto cash-no-print" data-testid="cash-portfolio">
          <table className="w-full min-w-[860px] table-fixed text-[12px]">
            <colgroup>
              <col className="w-[22%]" />
              <col className="w-[13%]" />
              <col className="w-[13%]" />
              <col className="w-[13%]" />
              <col className="w-[13%]" />
              <col className="w-[13%]" />
              <col className="w-[13%]" />
            </colgroup>
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-gray-400 text-left border-b border-gray-100">
                <th className="truncate px-2 py-1 font-semibold">Portfolio</th>
                <th className="px-2 py-1 text-right font-semibold">Opening</th>
                <th className="px-2 py-1 text-right font-semibold" title="Paid inflows only">In (paid)</th>
                <th className="px-2 py-1 text-right font-semibold" title="Paid outflows only">Out (paid)</th>
                <th className="px-2 py-1 text-right font-semibold">Today</th>
                <th className="px-2 py-1 text-right font-semibold" title="Full-ledger forecast — not available today">Future</th>
                <th className="px-2 py-1 text-right font-semibold" title="Held cash after dues and reserves">Deployable</th>
              </tr>
            </thead>
            <tbody>
              {portfolio.developments.map((d) => (
                <tr key={d.id} className="border-t border-gray-50">
                  <td className="truncate px-2 py-1 font-medium text-gray-800">{d.name}</td>
                  <MoneyCell>{money(d.opening)}</MoneyCell>
                  <MoneyCell className="text-emerald-700">{money(d.cashIn)}</MoneyCell>
                  <MoneyCell>{money(d.cashOut)}</MoneyCell>
                  <MoneyCell className={d.cashTodayNegative ? 'text-red-700' : ''}>{money(d.cashToday)}</MoneyCell>
                  <MoneyCell>{money(d.futureCash)}</MoneyCell>
                  <MoneyCell>{money(d.deployableCash)}</MoneyCell>
                </tr>
              ))}
              {portfolio.developments.length > 1 && (
                <tr className="border-t border-gray-200 font-semibold">
                  <td className="truncate px-2 py-1">Total</td>
                  <MoneyCell>{money(portfolio.opening)}</MoneyCell>
                  <MoneyCell>{money(portfolio.cashIn)}</MoneyCell>
                  <MoneyCell>{money(portfolio.cashOut)}</MoneyCell>
                  <MoneyCell>{money(portfolio.cashToday)}</MoneyCell>
                  <MoneyCell>{money(portfolio.futureCash)}</MoneyCell>
                  <MoneyCell>{money(portfolio.deployableCash)}</MoneyCell>
                </tr>
              )}
            </tbody>
          </table>
          <p className="px-2 py-1 text-[10px] text-gray-400">
            Deployable = cash today − due outflows − reserves. Expected sales are not spendable.
          </p>
        </section>
      )}
    </div>
  );
}

export function EditItemRow({ draft, entities, isNew = false, onChange, onSave, onCancel, onDuplicate, onDelete }) {
  // Several controls can change before React renders again (for example, a
  // pasted or imported ledger row). Build each patch from the latest draft so
  // a later field update cannot restore the initial date or discard a prior
  // field value.
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const set = (patch) => onChange((current) => {
    const next = { ...current, ...patch };
    draftRef.current = next;
    return next;
  });

  const flushAmountFromTarget = (target) => {
    if (!target || target.getAttribute?.('data-testid') !== 'cash-item-amount') return;
    const raw = String(target.value ?? '').trim().replace(',', '.');
    if (raw === '' || raw === '-' || raw === '.' || raw.endsWith('.')) return;
    const n = Number(raw);
    if (!Number.isFinite(n)) return;
    set({ amount: Math.abs(n) });
  };

  // Pad dotted display dates only (6.8.2025 → 06.08.2025). Never convert ISO
  // strings — the editor save path requires the DD.MM.YYYY workflow.
  const normalizeDisplayDateDraft = () => {
    if (!isCashDisplayDateInput(draftRef.current.date)) return;
    const display = formatCashDisplayDate(draftRef.current.date);
    if (display && display !== draftRef.current.date) set({ date: display });
  };

  const commitSave = () => onSave(draftRef.current);

  const onRowKeyDownCapture = (e) => {
    if (e.key !== 'Enter' || e.nativeEvent.isComposing) return;
    if (e.target.closest('button')) return;
    // Prevent amount-input blur-on-Enter and save synchronously so automation
    // does not race a focus loss / microtask.
    e.preventDefault();
    e.stopPropagation();
    flushAmountFromTarget(e.target);
    normalizeDisplayDateDraft();
    commitSave();
  };

  return (
    <tr
      className="cash-no-print border-t border-indigo-100 bg-indigo-50/40"
      data-testid="cash-item-sheet" data-cash-print-hide="1"
      data-cash-row-id={draft.id}
      data-cash-state="editing"
      data-cash-new={isNew ? 'true' : 'false'}
      data-cash-date-format="DD.MM.YYYY"
      aria-label={isNew ? 'New cash row editor' : 'Cash row editor'}
      onKeyDownCapture={onRowKeyDownCapture}
    >
      <td className="overflow-hidden px-1 py-0.5">
        <CashTextInput
          size="cell"
          name="date"
          value={draft.date}
          onChange={(date) => set({ date })}
          onBlur={() => {
            if (!isCashDisplayDateInput(draftRef.current.date)) return;
            const display = formatCashDisplayDate(draftRef.current.date);
            if (display) set({ date: display });
          }}
          placeholder="DD.MM.YYYY"
          aria-label={CASH_ITEM_FIELD_LABELS.date}
          testId="cash-item-date"
        />
      </td>
      <td className="overflow-hidden px-1 py-0.5">
        <CashSelect
          size="cell"
          name="direction"
          value={draft.direction}
          onChange={(direction) => set({ direction })}
          aria-label={CASH_ITEM_FIELD_LABELS.direction}
          testId="cash-item-direction"
        >
          {CASH_DIRECTIONS.map((d) => <option key={d} value={d}>{DIRECTION_LABEL[d]}</option>)}
        </CashSelect>
      </td>
      <td className="overflow-hidden px-1 py-0.5">
        <CashSelect
          size="cell"
          name="entity"
          value={draft.entity}
          onChange={(entity) => set({ entity })}
          aria-label={CASH_ITEM_FIELD_LABELS.entity}
          testId="cash-item-entity"
        >
          {entities.map((name) => <option key={name} value={name}>{name}</option>)}
        </CashSelect>
      </td>
      <td className="overflow-hidden px-1 py-0.5">
        <CashTextInput
          size="cell"
          name="counterparty"
          value={draft.counterparty}
          onChange={(counterparty) => set({ counterparty })}
          aria-label={CASH_ITEM_FIELD_LABELS.counterparty}
          testId="cash-item-counterparty"
        />
      </td>
      <td className="overflow-hidden px-1 py-0.5">
        <CashSelect
          size="cell"
          name="category"
          value={draft.categoryKey}
          onChange={(categoryKey) => set({ categoryKey })}
          aria-label={CASH_ITEM_FIELD_LABELS.category}
          testId="cash-item-category"
        >
          {BASELINE_CATEGORY_DEFS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </CashSelect>
      </td>
      <td className="overflow-hidden px-1 py-0.5">
        <CashAmountInput
          size="cell"
          name="amount"
          value={draft.amount}
          onChange={(amount) => set({ amount })}
          aria-label={CASH_ITEM_FIELD_LABELS.amount}
          testId="cash-item-amount"
        />
      </td>
      <td className="overflow-hidden px-1 py-0.5">
        <CashSelect
          size="cell"
          name="status"
          value={draft.status}
          onChange={(status) => set({ status })}
          aria-label={CASH_ITEM_FIELD_LABELS.status}
          testId="cash-item-status"
        >
          {CASH_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
        </CashSelect>
      </td>
      <td className="overflow-hidden px-1 py-0.5">
        <CashTextInput
          size="cell"
          name="note"
          value={draft.note}
          onChange={(note) => set({ note })}
          aria-label={CASH_ITEM_FIELD_LABELS.note}
          testId="cash-item-note"
        />
      </td>
      <td className="px-1 py-0.5">
        <div className="flex items-center justify-end gap-0.5" data-testid="cash-item-actions">
          {onDuplicate ? (
            <button
              type="button"
              className="p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
              data-testid="cash-item-duplicate"
              onClick={onDuplicate}
              aria-label={CASH_ITEM_FIELD_LABELS.duplicate}
            >
              <CopySimple className="w-3.5 h-3.5" />
            </button>
          ) : null}
          {onDelete ? (
            <button
              type="button"
              className="p-1 text-red-500 hover:bg-red-50"
              data-testid="cash-item-delete"
              onClick={onDelete}
              aria-label={CASH_ITEM_FIELD_LABELS.delete}
            >
              <Trash className="w-3.5 h-3.5" />
            </button>
          ) : null}
          <button
            type="button"
            className="p-1 text-gray-400 hover:bg-gray-100"
            data-testid="cash-item-cancel"
            onClick={onCancel}
            aria-label={CASH_ITEM_FIELD_LABELS.cancel}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function EvaluationCashTable({ evaluation, money }) {
  if (!evaluation) return null;
  const cell = (row, value, { emphasize = false, tone } = {}) => {
    if (row.format === 'percent') {
      const shown = value == null || !Number.isFinite(Number(value)) ? '—' : formatPercent(value);
      return (
        <td className={`overflow-hidden px-3 py-1 text-right tabular-nums ${emphasize ? 'font-semibold' : ''} ${tone || ''}`}>
          <span className="block truncate">{shown}</span>
        </td>
      );
    }
    if (value == null) {
      return <td className="overflow-hidden px-3 py-1 text-right text-gray-300">—</td>;
    }
    return <MoneyCell className={`${emphasize ? 'font-semibold' : ''} ${tone || ''}`}>{money(value)}</MoneyCell>;
  };

  return (
    <section className="mt-2 border border-gray-200 bg-white overflow-x-auto" data-testid="cash-reconciliation">
      <table className="w-full min-w-[800px] table-fixed text-[12px]">
        <colgroup>
          <col className="w-[28%]" />
          <col className="w-[18%]" />
          <col className="w-[18%]" />
          <col className="w-[18%]" />
          <col className="w-[18%]" />
        </colgroup>
        <thead>
          <tr className="text-[10px] uppercase tracking-wide text-gray-400 text-left border-b border-gray-100">
            <th className="truncate px-2 py-1 font-semibold">Budget vs cash</th>
            <th className="px-2 py-1 text-right font-semibold">Budget</th>
            <th className="px-2 py-1 text-right font-semibold">Paid</th>
            <th className="truncate px-2 py-1 text-right font-semibold">Expected/due</th>
            <th className="px-2 py-1 text-right font-semibold">Remaining</th>
          </tr>
        </thead>
        <tbody>
          {evaluation.rows.map((row) => {
            if (row.kind === 'section') {
              const profit = row.tone === 'profit';
              return (
                <tr key={row.key} className={profit ? 'border-t-2 border-emerald-300 bg-emerald-50/40' : 'border-t border-gray-200 bg-gray-50'}>
                  <td colSpan={5} className={`px-2 py-1 text-[11px] font-bold ${profit ? 'text-emerald-900 uppercase tracking-wide' : 'text-gray-800'}`}>
                    {row.label}
                  </td>
                </tr>
              );
            }
            if (row.kind === 'group') {
              return (
                <tr key={row.key} className="border-t border-gray-50">
                  <td colSpan={5} className="px-2 pt-2 pb-0.5 text-[10px] font-bold uppercase tracking-wide text-gray-400">
                    {row.label}
                  </td>
                </tr>
              );
            }
            const total = row.kind === 'total';
            const profitLine = row.key === 'pat' || row.key === 'net';
            const tone = profitLine
              ? (asPositive(row.budget) ? 'text-emerald-700' : 'text-red-700')
              : '';
            return (
              <tr
                key={row.key}
                className={`border-t ${total ? 'border-gray-200' : 'border-gray-50'} ${row.overBudget ? 'bg-red-50' : ''} ${row.tone === 'profit' ? 'bg-emerald-50/20' : ''}`}
                data-testid={row.testId}
              >
                <td className={`truncate px-2 py-1 ${row.indent ? 'pl-5' : ''} ${total ? 'font-semibold text-gray-900' : 'text-gray-800'}`}>
                  {row.label}
                </td>
                {cell(row, row.budget, { emphasize: total, tone })}
                {cell(row, row.paid)}
                {cell(row, row.open)}
                {cell(row, row.remaining, { emphasize: true, tone })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

function asPositive(n) {
  return !(Number(n) < 0);
}

function EntitiesTable({
  rows,
  money,
  canDelete,
  showBalances = true,
  showEntities = true,
  onAdd,
  onRename,
  onRole,
  onOpening,
  onRemove,
  reserves,
  onReserves,
  showReserves,
  onToggleReserves,
}) {
  const printTitle = showBalances && showEntities
    ? 'Entities & opening balances'
    : showBalances
      ? 'Opening / account balances'
      : 'Entities';
  const screenTitle = showEntities ? 'Entities' : 'Opening / account balances';
  const screenHint = showEntities
    ? (showBalances ? 'Legal vehicles and opening balances' : 'Legal vehicles on this development')
    : 'Opening balances and designated reserves';

  // Name column always labels rows. Identity controls (role/edit/add/delete + In/Out/Today/Future)
  // follow `showEntities`. Opening amounts + reserves follow `showBalances`.
  const minWidth = showEntities && showBalances
    ? 'min-w-[860px]'
    : showEntities
      ? 'min-w-[740px]'
      : 'min-w-[360px]';

  return (
    <section
      className="mt-2 border border-gray-200 bg-white"
      data-testid={showEntities ? 'cash-entities' : 'cash-balances'}
      data-cash-section-balances={showBalances ? 'true' : 'false'}
      data-cash-section-entities={showEntities ? 'true' : 'false'}
    >
      <div className="flex items-center gap-2 px-2 py-1 border-b border-gray-100 cash-no-print">
        <h2 className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">{screenTitle}</h2>
        <p className="text-[11px] text-gray-400 truncate hidden sm:block">{screenHint}</p>
        {showEntities ? (
          <button
            type="button"
            onClick={onAdd}
            className="ml-auto inline-flex items-center gap-1 h-7 px-2 text-[11px] font-semibold text-indigo-700 hover:bg-indigo-50"
            data-testid="cash-add-entity"
          >
            <Plus weight="bold" className="w-3 h-3" />
            Entity
          </button>
        ) : null}
      </div>
      <div className="hidden cash-print-only px-2 py-1 border-b border-gray-100 text-[10px] uppercase tracking-wide text-gray-500 font-semibold">
        {printTitle}
      </div>
      <div className="overflow-x-auto">
        <table className={`w-full ${minWidth} table-fixed text-[12px]`}>
          <colgroup>
            <col className="w-[9rem]" />
            {showEntities ? <col className="w-[8rem]" /> : null}
            {showBalances ? <col className="w-[8rem]" /> : null}
            {showEntities ? (
              <>
                <col />
                <col />
                <col />
                <col />
                <col className="w-[2.75rem]" />
              </>
            ) : null}
          </colgroup>
          <thead>
            <tr className="text-[10px] uppercase tracking-wide text-gray-400 text-left border-b border-gray-100">
              <th className="px-2 py-1 font-semibold">Name</th>
              {showEntities ? <th className="px-2 py-1 font-semibold">Role</th> : null}
              {showBalances ? <th className="px-2 py-1 text-right font-semibold">Opening</th> : null}
              {showEntities ? (
                <>
                  <th className="px-2 py-1 text-right font-semibold">In</th>
                  <th className="px-2 py-1 text-right font-semibold">Out</th>
                  <th className="px-2 py-1 text-right font-semibold">Today</th>
                  <th
                    className="px-2 py-1 text-right font-semibold"
                    title="Full-ledger forecast — not available today"
                  >
                    Future
                  </th>
                  <th className="px-1 py-1 font-semibold" />
                </>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-t border-gray-50" data-testid={`cash-entity-${row.id}`}>
                <td className="overflow-hidden px-1 py-0.5">
                  {showEntities ? (
                    <CashTextInput
                      size="cell"
                      value={row.name}
                      onChange={(name) => onRename(row.id, name)}
                      testId={`cash-entity-name-${row.id}`}
                    />
                  ) : (
                    <span className="block truncate px-1.5 py-1 text-gray-800" data-testid={`cash-entity-name-${row.id}`}>
                      {row.name}
                    </span>
                  )}
                </td>
                {showEntities ? (
                  <td className="overflow-hidden px-1 py-0.5">
                    <CashSelect
                      size="cell"
                      value={row.role}
                      onChange={(role) => onRole(row.id, role)}
                      testId={`cash-entity-role-${row.id}`}
                    >
                      {ENTITY_ROLES.map((role) => (
                        <option key={role} value={role}>{ENTITY_ROLE_LABEL[role]}</option>
                      ))}
                    </CashSelect>
                  </td>
                ) : null}
                {showBalances ? (
                  <td className="overflow-hidden px-1 py-0.5">
                    <CashAmountInput
                      size="cell"
                      value={row.opening}
                      onChange={(amount) => onOpening(row.id, amount)}
                      testId={`cash-entity-opening-${row.id}`}
                      aria-label={`Opening cash for ${row.name}`}
                    />
                  </td>
                ) : null}
                {showEntities ? (
                  <>
                    <MoneyCell className="text-emerald-700">{money(row.cashIn)}</MoneyCell>
                    <MoneyCell>{money(row.cashOut)}</MoneyCell>
                    <MoneyCell className={`font-semibold ${row.cashTodayNegative ? 'text-red-700' : ''}`}>
                      {money(row.cashToday)}
                    </MoneyCell>
                    <MoneyCell testId={`cash-entity-future-${row.id}`}>
                      {money(row.futureCash)}
                    </MoneyCell>
                    <td className="px-1 py-0.5 cash-no-print">
                      {canDelete ? (
                        <button
                          type="button"
                          className="p-1 text-gray-400 hover:text-red-600"
                          data-testid={`cash-entity-delete-${row.id}`}
                          onClick={() => onRemove(row.id)}
                          aria-label={`Remove ${row.name}`}
                        >
                          <Trash className="w-3.5 h-3.5" />
                        </button>
                      ) : null}
                    </td>
                  </>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showBalances ? (
        <>
          <div className="flex items-center gap-2 px-2 py-1 border-t border-gray-100 cash-no-print">
            <button
              type="button"
              className="text-[11px] text-gray-500 hover:text-indigo-700"
              onClick={onToggleReserves}
              data-testid="cash-opening"
            >
              {showReserves ? 'Hide reserves' : 'Designated reserves'}
            </button>
            {showReserves ? (
              <CashAmountInput
                size="cell"
                value={reserves}
                onChange={onReserves}
                testId="cash-opening-reserves"
                aria-label="Designated reserves"
              />
            ) : (
              <span className="text-[11px] text-gray-400 tabular-nums">{money(reserves)}</span>
            )}
          </div>
          <div className="hidden cash-print-only px-2 py-1 border-t border-gray-100 text-[11px] text-gray-600">
            Designated reserves: <span className="tabular-nums font-medium">{money(reserves)}</span>
          </div>
        </>
      ) : null}
    </section>
  );
}
